import { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import { useSession } from './useSession.ts';
import { supabase } from '../services/supabaseClient.ts';

export interface SubscriptionData {
  plan: string;
  scans_used: number;
  scan_limit: number | null; // null = unlimited
  mb_limit: number;
  quota_left: number | null; // null = unlimited
  ai_fixes_used: number;
  ai_fixes_limit: number | null; // null = no AI fixes available
  ai_fixes_left: number | null; // null = no AI fixes available
  period_end?: string;
  status: 'active' | 'inactive' | 'cancelled';
}

export interface UseSubscriptionReturn {
  data: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  canScan: boolean;
  hasCredits: boolean;
  displayText: string;
}

/**
 * Hook for subscription status using new subscriptions table
 * Replaces useScanCredits with real-time updates
 */
export function useSubscription(): UseSubscriptionReturn {
  const { session, loading: sessionLoading } = useSession();
  const [error, setError] = useState<string | null>(null);

  // SWR fetcher function
  const fetcher = useCallback(async (url: string): Promise<SubscriptionData> => {
    console.log('[useSubscription] Fetching from:', url);
    
    if (!session?.access_token) {
      throw new Error('No access token available');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('[useSubscription] Received data:', data);
    return data;
  }, [session?.access_token]);

  // SWR hook with conditional fetching
  const swrKey = session?.user?.id 
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-status`
    : null;

  const { 
    data, 
    error: swrError, 
    isLoading, 
    mutate 
  } = useSWR<SubscriptionData>(
    swrKey,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: 0, // We'll use realtime updates instead
      errorRetryCount: 3,
      onError: (err: Error) => {
        console.error('[useSubscription] SWR error:', err);
        setError(err.message);
      },
      onSuccess: () => {
        setError(null);
      }
    }
  );

  // Set up realtime subscription for updates
  useEffect(() => {
    if (!session?.user?.id || !data) {
      console.log('[useSubscription] Skipping realtime setup - no user or data');
      return;
    }

    const channelName = `sub_status:${session.user.id}`;
    console.log('[useSubscription] Setting up realtime channel:', channelName);

    const channel = supabase
      .channel(channelName)
      .on(
        'broadcast',
        { event: 'updated' },
        (payload) => {
          console.log('[useSubscription] Received realtime update:', payload);
          mutate(); // Revalidate the data
        }
      )
      .subscribe((status) => {
        console.log('[useSubscription] Channel subscription status:', status);
      });

    // Cleanup function
    return () => {
      console.log('[useSubscription] Cleaning up realtime channel');
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, data, mutate]);

  // Manual refresh function
  const refresh = useCallback(async () => {
    console.log('[useSubscription] Manual refresh triggered');
    await mutate();
  }, [mutate]);

  // Derived values
  const canScan = data ? (data.quota_left === null || data.quota_left > 0) : false;
  const hasCredits = canScan;
  
  // Display text formatting
  const displayText = data 
    ? data.quota_left === null 
      ? '999+ scans' 
      : `${data.quota_left} scan${data.quota_left !== 1 ? 's' : ''}`
    : isLoading 
      ? 'Loading...' 
      : '0 scans';

  console.log('[useSubscription] Hook state:', {
    dataPresent: !!data,
    loading: sessionLoading || isLoading,
    error: error || swrError?.message,
    canScan,
    hasCredits,
    displayText,
  });

  return {
    data: data ?? null,
    loading: sessionLoading || isLoading,
    error: error || swrError?.message || null,
    refresh,
    canScan,
    hasCredits,
    displayText,
  };
} 